import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../services/api";
import Menu from "../../comum/Menu/menu.jsx";
import Footer from "../../comum/Footer";
import Swal from 'sweetalert2';
import { Container, Card, Form, Button, Spinner, Badge } from 'react-bootstrap';
import './checklist.css';

function LoadingOverlay({ show, message }) {
    if (!show) return null;
    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
            backgroundColor: 'rgba(255,255,255,0.9)', zIndex: 99999,
            display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'
        }}>
            <Spinner animation="border" style={{ color: '#8b0000', width: '3.5rem', height: '3.5rem' }} />
            <span className="mt-3 fw-bold text-danger italic">{message || "Processando, aguarde..."}</span>
        </div>
    );
}

function calcularDistancia(lat1, lon1, lat2, lon2) {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

function FazerChecklist() {
    const [lojas, setLojas] = useState([]);
    const [lojaSelecionada, setLojaSelecionada] = useState("");
    const [itensChecklist, setItensChecklist] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("");
    const navigate = useNavigate();
    const chapa = localStorage.getItem("chapa");
    const permiteCheckList = localStorage.getItem("CHECKLIST");

    useEffect(() => {
        if (!chapa) {
            navigate("/arbor/login");
            return;
        }
        else if (permiteCheckList !== "S") {
            Swal.fire({ icon: 'error', title: 'Acesso Negado', text: 'Não é permitido acessar o CheckList.', confirmButtonText: 'OK' })
                .then(() => navigate("/arbor/home"));
        }
        
        const carregarLojasEGeolocalizar = async () => {
            setLoading(true);
            setLoadingMessage("Buscando sua localização...");

            try {
                const posicao = await new Promise((resolve, reject) => {
                    navigator.geolocation.getCurrentPosition(resolve, reject, {
                        enableHighAccuracy: true,
                        timeout: 5000,
                        maximumAge: 0
                    });
                });
                const { latitude, longitude } = posicao.coords;

                setLoadingMessage("Carregando lista de lojas...");
                const res = await api.get("lojas/localizacao");
                const lojasPermitidas = res.data.filter(l => l.cod_emp < 50 && l.cod_emp !== 1 && l.cod_emp !== 23);
                setLojas(lojasPermitidas);
                
                if (lojasPermitidas.length > 0) {
                    let lojaMaisProxima = null;
                    let menorDistancia = Infinity;

                    lojasPermitidas.forEach(loja => {
                        if (loja.latitude && loja.longitude) {
                            const distancia = calcularDistancia(latitude, longitude, loja.latitude, loja.longitude);
                            if (distancia < menorDistancia) {
                                menorDistancia = distancia;
                                lojaMaisProxima = loja;
                            }
                        }
                    });

                    if (lojaMaisProxima) {
                        Swal.fire({
                            title: 'Loja Encontrada!',
                            text: `Parece que você está na Loja ${String(lojaMaisProxima.cod_emp).padStart(2, '0')} - ${lojaMaisProxima.razao}.`,
                            icon: 'info',
                            confirmButtonText: 'Continuar'
                        });
                        setLojaSelecionada(String(lojaMaisProxima.cod_emp));
                    } else {
                        Swal.fire('Aviso', 'Não foi possível encontrar uma loja próxima com as coordenadas disponíveis. Por favor, selecione manualmente.', 'warning');
                    }
                }

            } catch (error) {
                console.error("Erro de geolocalização ou ao carregar dados:", error);
                if (error.code === 1) {
                    Swal.fire('Aviso', 'Permissão de geolocalização negada. Por favor, selecione a loja manualmente.', 'warning');
                } else {
                    Swal.fire('Erro', 'Não foi possível detectar sua localização ou carregar as lojas. Por favor, selecione manualmente.', 'error');
                }
                try {
                    const res = await api.get("lojas/localizacao");
                    const lojasPermitidas = res.data.filter(l => l.cod_emp < 50 && l.cod_emp !== 1 && l.cod_emp !== 23);
                    setLojas(lojasPermitidas);
                } catch (e) {
                    console.error("Erro ao carregar lojas após falha na geolocalização:", e);
                }
            } finally {
                setLoading(false);
            }
        };

        carregarLojasEGeolocalizar();
    }, [chapa, navigate, permiteCheckList]);

    useEffect(() => {
        const carregarItens = async () => {
            if (!lojaSelecionada) {
                setItensChecklist([]);
                return;
            }
            try {
                setLoading(true);
                setLoadingMessage("Carregando itens de checklist...");
                const res = await api.get(`/checklist/loja/${lojaSelecionada}/itens`);
                const itensComEstado = res.data.map(item => ({
                    ...item,
                    status: '',
                    comentario: ''
                }));
                const itensAgrupados = itensComEstado.reduce((acc, item) => {
                    const tipo = item.tipo_equipamento || 'Outros';
                    if (!acc[tipo]) {
                        acc[tipo] = [];
                    }
                    acc[tipo].push(item);
                    return acc;
                }, {});
                setItensChecklist(itensAgrupados);
            } catch (error) {
                console.error("Erro ao carregar itens da loja:", error);
                const errorMessage = error.response?.data?.erro || "Não foi possível carregar os itens associados a esta loja.";
                Swal.fire('Erro', errorMessage, 'error');
                setItensChecklist([]);
            } finally {
                setLoading(false);
            }
        };
        carregarItens();
    }, [lojaSelecionada]);

    const handleItemChange = (itemId, campo, valor) => {
        setItensChecklist(prevItens => {
            const novosItens = { ...prevItens };
            for (const tipo in novosItens) {
                const itemIndex = novosItens[tipo].findIndex(item => item.id_item === itemId);
                if (itemIndex > -1) {
                    novosItens[tipo][itemIndex] = { ...novosItens[tipo][itemIndex], [campo]: valor };
                    break;
                }
            }
            return novosItens;
        });
    };

    const handleFinalizarChecklist = async () => {
        if (!lojaSelecionada) {
            Swal.fire('Aviso', 'Selecione uma loja para finalizar o checklist.', 'warning');
            return;
        }

        const todosItens = Object.values(itensChecklist).flat();
        const itensNaoAvaliados = todosItens.filter(item => !item.status);

        if (itensNaoAvaliados.length > 0) {
            Swal.fire('Aviso', 'Todos os itens devem ser avaliados.', 'warning');
            return;
        }

        try {
            setLoading(true);
            setLoadingMessage("Finalizando checklist...");
            
            const now = new Date();
            const dataExecucao = now.toISOString().slice(0, 10);
            const horaExecucao = now.toTimeString().slice(0, 5);

            const payload = {
                idLoja: parseInt(lojaSelecionada),
                idUsuario: chapa,
                dataExecucao: dataExecucao,
                horaExecucao: horaExecucao,
                itens: todosItens.map(item => ({
                    idItem: item.id_item,
                    status: item.status,
                    comentario: item.comentario
                }))
            };
            
            const res = await api.post('/checklist/finalizar', payload);
            
            Swal.fire('Sucesso!', res.data.mensagem, 'success');
            setLojaSelecionada(""); 
            setItensChecklist([]);
            
        } catch (error) {
            console.error("Erro ao finalizar checklist:", error);
            const errorMessage = error.response?.data?.erro || "Não foi possível finalizar o checklist.";
            Swal.fire('Erro', errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
            <Menu modulo="ARBOR" />
            <Container fluid className="px-4 mt-4 pt-4 mb-5" style={{ flex: 1, maxWidth: '900px' }}>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="fw-bold text-dark m-0">
                        <i className="fa-solid fa-clipboard-check me-2 text-danger"></i>Executar Checklist
                    </h4>
                </div>

                <Card className="shadow-sm border-0 bg-white rounded-3 mb-4 p-3">
                    <Form.Group className="mb-0">
                        <Form.Label className="fw-bold small text-muted">SELECIONE A LOJA</Form.Label>
                        <Form.Select
                            className="form-select fw-bold"
                            value={lojaSelecionada}
                            onChange={e => setLojaSelecionada(e.target.value)}
                        >
                            <option value="">--- Selecione uma loja ---</option>
                            {lojas.map(l => (
                                <option key={l.cod_emp} value={l.cod_emp}>
                                    Loja {String(l.cod_emp).padStart(2, '0')} - {l.razao}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Card>

                {lojaSelecionada && Object.keys(itensChecklist).length > 0 && (
                    <div className="checklist-container">
                        {Object.entries(itensChecklist).map(([tipo, itens]) => (
                            <Card key={tipo} className="shadow-sm border-0 bg-white rounded-3 mb-4 p-3">
                                <h5 className="fw-bold text-danger border-bottom pb-2 mb-3">
                                    <i className="fa-solid fa-list-check me-2"></i>{tipo}
                                </h5>
                                {itens.map(item => (
                                    <div key={item.id_item} className="mb-3 p-3 border rounded-3 bg-light">
                                        <h6 className="fw-bold text-dark mb-3">{item.descricao}</h6>
                                        
                                        <div className="d-flex gap-4 mb-2">
                                            <Form.Check
                                                type="radio"
                                                id={`status-s-${item.id_item}`}
                                                name={`status-${item.id_item}`}
                                                label={<span><i className="fa-solid fa-circle-check text-success me-1"></i> Tudo Certo 👍🏻</span>}
                                                checked={item.status === 'S'}
                                                onChange={() => handleItemChange(item.id_item, 'status', 'S')}
                                            />
                                            <Form.Check
                                                type="radio"
                                                id={`status-e-${item.id_item}`}
                                                name={`status-${item.id_item}`}
                                                label={<span><i className="fa-solid fa-circle-xmark text-danger me-1"></i> Problema Encontrado 👎🏻</span>}
                                                checked={item.status === 'E'}
                                                onChange={() => handleItemChange(item.id_item, 'status', 'E')}
                                            />
                                        </div>

                                        {item.status === 'E' && (
                                            <div className="mt-2">
                                                <Form.Control
                                                    as="textarea"
                                                    rows={2}
                                                    placeholder="Descreva os detalhes do problema em questão..."
                                                    value={item.comentario}
                                                    onChange={e => handleItemChange(item.id_item, 'comentario', e.target.value)}
                                                />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </Card>
                        ))}

                        <div className="d-grid gap-2 mb-4">
                            <Button
                                variant="danger"
                                className="btn-arbor-primary py-2 fw-bold fs-6 shadow-sm"
                                onClick={handleFinalizarChecklist}
                                disabled={loading}
                            >
                                <i className="fa-solid fa-paper-plane me-2"></i>Finalizar Checklist
                            </Button>
                        </div>
                    </div>
                )}

                {!lojaSelecionada && (
                    <Card className="border-0 shadow-sm p-5 text-center text-muted rounded-3 bg-white">
                        <i className="fa-solid fa-store display-4 mb-3 text-secondary"></i>
                        Selecione uma loja para carregar os itens do checklist.
                    </Card>
                )}

                {lojaSelecionada && Object.keys(itensChecklist).length === 0 && !loading && (
                    <Card className="border-0 shadow-sm p-5 text-center text-muted rounded-3 bg-white">
                        <i className="fa-solid fa-circle-exclamation display-4 mb-3 text-warning"></i>
                        Nenhum item de checklist associado a esta loja.
                    </Card>
                )}
            </Container>

            <LoadingOverlay show={loading} message={loadingMessage} />
            <Footer />
        </div>
    );
}

export default FazerChecklist;